'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, useActiveWallet, useAuth, useClaimBackerFees, useGetMarketBackerEarnings, useTurnkeySigningDeps, useWallets } from '@morfi/backend-sdk-v2/client';
import { isMarketResolved } from '@morfi/contract-sdk';
import type { BackerRewardsPool } from '@/components/market/backers/backer-earnings';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
const D9_SCALE_BIGINT: bigint = BigInt(D9_SCALE);
interface UseBackerRewardsParams {
  marketPubkey: string;
  marketStatus: string;
  enabled?: boolean;
}
interface UseBackerRewardsResult {
  rewardPool: BackerRewardsPool | null;
  /** Whether viewer identity or its wallet-scoped reward row is still loading. */
  isPending: boolean;
  /** Vault total in display units (D6 decoded). Always populated. */
  vaultAmount: number;
  /** Card headline: live vault before claim, lifetime pool after a full claim. */
  headlineAmount: number;
  /** Lifetime fees collected (D6 decoded). Always populated. */
  totalCollected: number;
  /** D6 string of vault for AnimatedNumber tick-up. */
  vaultD6: string;
  /** User's pro-rata earnings in display units. 0 if not a backer. */
  earnedAmount: number;
  /** D6 string of earned for AnimatedNumber. */
  earnedD6: string;
  /** User's share-token balance (D6). 0 if not a backer. */
  userShareD6: string;
  /** Cumulative claimed amount (D6 decoded). 0 if no claims. */
  claimedTotal: number;
  /** Current token share, or claimed payout / lifetime pool after a full claim. */
  userSharePct: number;
  /** Whether the user currently holds or has already claimed backer rewards. */
  isBacker: boolean;
  /** Whether the user has already claimed any backer rewards for this market. */
  hasClaimed: boolean;
  /** Claim enabled — unclaimed shares + market is resolved/graduated. */
  canClaim: boolean;
  /** Whether the claim mutation is currently in flight. */
  isClaiming: boolean;
  /** Fire the claim — burns the user's entire balance. */
  claim: () => void;
}
function d6ToDisplay(value: string | undefined): number {
  if (value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n / D6_SCALE;
}

/**
 * Orchestrates all backer-rewards state for the market detail page.
 *
 * Combines `useGetMarketBackerEarnings` (vault, supply, userShare, claimed)
 * with `useClaimBackerFees` (mutation) and the market status predicate
 * (`isMarketResolved`) into a single set of display-ready values.
 * Components consuming this hook do not need to call any SDK hook
 * directly. Vault total + total collected are always populated so the
 * card renders the marketing surface even for non-backers.
 */
export function useBackerRewards(t0) {
  const $ = _c(75);
  const {
    marketPubkey,
    marketStatus,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    user,
    isLoading: isAuthLoading
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const {
    state: multiWalletState,
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  let primaryWalletPubkey;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== marketPubkey || $[1] !== multiWalletEnabled || $[2] !== primaryWallet || $[3] !== user?.id || $[4] !== wallets) {
    const accountWalletPubkeys = wallets.map(_temp);
    primaryWalletPubkey = primaryWallet !== null && primaryWallet.archivedAt === null && primaryWallet.isAvailable !== false ? primaryWallet.address : null;
    const walletPubkeys = multiWalletEnabled ? accountWalletPubkeys : primaryWalletPubkey === null ? [] : [primaryWalletPubkey];
    t6 = useGetMarketBackerEarnings;
    t2 = marketPubkey;
    if ($[11] !== multiWalletEnabled || $[12] !== primaryWalletPubkey) {
      t3 = multiWalletEnabled || primaryWalletPubkey === null ? {} : {
        wallet: primaryWalletPubkey
      };
      $[11] = multiWalletEnabled;
      $[12] = primaryWalletPubkey;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t4 = createBackerRewardScope(user?.id, walletPubkeys);
    t5 = walletPubkeys.join(",");
    $[0] = marketPubkey;
    $[1] = multiWalletEnabled;
    $[2] = primaryWallet;
    $[3] = user?.id;
    $[4] = wallets;
    $[5] = primaryWalletPubkey;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    primaryWalletPubkey = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["vaultD6", "supplyD6", "totalCollectedD6", "userShareD6", "earnedD6", "claimedTotalD6", "canClaim", "marketState"];
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t8 = {
      pubkey: t2,
      ...t3,
      rewardScope: t4,
      walletPubkeysCsv: t5,
      fields: t7
    };
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== enabled) {
    t9 = {
      enabled
    };
    $[20] = enabled;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const earnings = t6(t8, t9);
  const claim = useClaimBackerFees();
  const {
    activeWallet
  } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();
  const globalRow = earnings.data;
  const viewerRow = multiWalletEnabled || primaryWalletPubkey !== null ? globalRow : undefined;
  const vaultD6 = globalRow?.vaultD6 ?? "0";
  const supplyD6 = globalRow?.supplyD6 ?? "0";
  const userShareD6 = viewerRow?.userShareD6 ?? "0";
  const currentEarnedD6 = viewerRow?.earnedD6 ?? "0";
  const totalCollectedD6 = globalRow?.totalCollectedD6 ?? "0";
  const claimedTotalD6 = viewerRow?.claimedTotalD6 ?? "0";
  const ZERO = BigInt(0);
  let t10;
  try {
    let t11;
    if ($[22] !== userShareD6) {
      t11 = BigInt(userShareD6);
      $[22] = userShareD6;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t10 = t11;
  } catch {
    t10 = ZERO;
  }
  const userShareBn = t10;
  let t11;
  try {
    let t12;
    if ($[24] !== claimedTotalD6) {
      t12 = BigInt(claimedTotalD6);
      $[24] = claimedTotalD6;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    t11 = t12;
  } catch {
    t11 = ZERO;
  }
  const claimedTotalBn = t11;
  let t12;
  try {
    let t13;
    if ($[26] !== supplyD6) {
      t13 = BigInt(supplyD6);
      $[26] = supplyD6;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = t13;
  } catch {
    t12 = ZERO;
  }
  const supplyBn = t12;
  let t13;
  try {
    let t14;
    if ($[28] !== totalCollectedD6) {
      t14 = BigInt(totalCollectedD6);
      $[28] = totalCollectedD6;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    t13 = t14;
  } catch {
    t13 = ZERO;
  }
  const totalCollectedBn = t13;
  const viewerStatePending = isAuthLoading || multiWalletState === "pending" || earnings.isPending;
  const hasUnclaimedShares = userShareBn > ZERO;
  const hasClaimed = claimedTotalBn > ZERO;
  const shareNumerator = hasUnclaimedShares ? userShareBn : claimedTotalBn;
  const shareDenominator = hasUnclaimedShares ? supplyBn : totalCollectedBn;
  const userSharePpb = shareDenominator === ZERO ? ZERO : shareNumerator * D9_SCALE_BIGINT / shareDenominator;
  const userSharePct = Number(userSharePpb) / D9_SCALE;
  const isBacker = hasUnclaimedShares || hasClaimed;
  let t14;
  if ($[30] !== enabled || $[31] !== hasUnclaimedShares || $[32] !== marketStatus || $[33] !== viewerStatePending) {
    t14 = enabled && !viewerStatePending && hasUnclaimedShares && isMarketResolved(marketStatus);
    $[30] = enabled;
    $[31] = hasUnclaimedShares;
    $[32] = marketStatus;
    $[33] = viewerStatePending;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const canClaim = t14;
  const earnedD6 = hasUnclaimedShares ? currentEarnedD6 : claimedTotalD6;
  const headlineD6 = hasClaimed && !hasUnclaimedShares ? totalCollectedD6 : vaultD6;
  let t15;
  if ($[35] !== activeWallet || $[36] !== canClaim || $[37] !== claim || $[38] !== marketPubkey || $[39] !== signingDeps || $[40] !== userShareD6) {
    t15 = () => {
      if (!canClaim || activeWallet === null || activeWallet === undefined) {
        return;
      }
      claim.mutate({
        marketAddress: marketPubkey,
        burnAmountD6: userShareD6,
        activeWallet: {
          id: activeWallet.id,
          address: activeWallet.address,
          type: activeWallet.type
        },
        signingDeps
      });
    };
    $[35] = activeWallet;
    $[36] = canClaim;
    $[37] = claim;
    $[38] = marketPubkey;
    $[39] = signingDeps;
    $[40] = userShareD6;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const fire = t15;
  let t16;
  if ($[42] !== earnings.isError || $[43] !== globalRow?.supplyD6 || $[44] !== globalRow?.vaultD6 || $[45] !== supplyD6 || $[46] !== vaultD6) {
    t16 = globalRow?.vaultD6 === undefined || globalRow?.supplyD6 === undefined || earnings.isError ? null : {
      vaultD6,
      supplyD6
    };
    $[42] = earnings.isError;
    $[43] = globalRow?.supplyD6;
    $[44] = globalRow?.vaultD6;
    $[45] = supplyD6;
    $[46] = vaultD6;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== vaultD6) {
    t17 = d6ToDisplay(vaultD6);
    $[48] = vaultD6;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== headlineD6) {
    t18 = d6ToDisplay(headlineD6);
    $[50] = headlineD6;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== totalCollectedD6) {
    t19 = d6ToDisplay(totalCollectedD6);
    $[52] = totalCollectedD6;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== earnedD6) {
    t20 = d6ToDisplay(earnedD6);
    $[54] = earnedD6;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== claimedTotalD6) {
    t21 = d6ToDisplay(claimedTotalD6);
    $[56] = claimedTotalD6;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== canClaim || $[59] !== claim.isPending || $[60] !== earnedD6 || $[61] !== fire || $[62] !== hasClaimed || $[63] !== isBacker || $[64] !== t16 || $[65] !== t17 || $[66] !== t18 || $[67] !== t19 || $[68] !== t20 || $[69] !== t21 || $[70] !== userShareD6 || $[71] !== userSharePct || $[72] !== vaultD6 || $[73] !== viewerStatePending) {
    t22 = {
      rewardPool: t16,
      isPending: viewerStatePending,
      vaultAmount: t17,
      headlineAmount: t18,
      totalCollected: t19,
      vaultD6,
      earnedAmount: t20,
      earnedD6,
      userShareD6,
      claimedTotal: t21,
      userSharePct,
      isBacker,
      hasClaimed,
      canClaim,
      isClaiming: claim.isPending,
      claim: fire
    };
    $[58] = canClaim;
    $[59] = claim.isPending;
    $[60] = earnedD6;
    $[61] = fire;
    $[62] = hasClaimed;
    $[63] = isBacker;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
    $[70] = userShareD6;
    $[71] = userSharePct;
    $[72] = vaultD6;
    $[73] = viewerStatePending;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
}
function _temp(wallet) {
  return wallet.address;
}